import React, { useState ,useEffect} from "react";
import {
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  AppBar,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { useDispatch, useSelector } from "react-redux";
import { ragSearchResume } from "../../../services/resumeService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { selectJob } from "../../../store/reducers/appreducer";
const ResumePoolSearch = () => {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [candidateCount, setCandidateCount] = useState(0);
  const [error, setError] = useState(null);

  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.appreducer.account.company_id);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    dispatch(selectJob(null));
  }, [dispatch]);

  // Helper function to safely render potentially problematic values
  const safeRender = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "object") return JSON.stringify(value);
    return value;
  };

  // Search Handler
  const handleSearch = async () => {
    if (!searchKeyword.trim()) return;

    setLoading(true);
    setError(null);
    try {
      console.log(companyId);
      const data = {
        query: searchKeyword,
        company_id: companyId,
      };

      const Response = await ragSearchResume(data);

      if (!Response.error) {
        setSearchResults(Response.Candidates || []);
        setCandidateCount(Response["Candidates Fetched"] || 0);
        dispatch(
          showAppAlert({
            show: true,
            alertType: "success",
            alertMessage: "Search completed successfully",
          })
        );
      } else {
        setSearchResults([]);
        setCandidateCount(0);
        dispatch(
          showAppAlert({
            show: true,
            alertType: "info",
            alertMessage: "No matching candidates found for your search",
          })
        );
      }
    } catch (error) {
      console.error("Error searching resumes:", error);
      setSearchResults([]);
      setCandidateCount(0);
      dispatch(
        showAppAlert({
          show: true,
          alertType: "error",
          alertMessage: error?.message || "Error searching resumes",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClear = () => {
    setSearchKeyword("");
    setSearchResults([]);
    setCandidateCount(0);
    setError(null);
  };

  return (
    <Box
      sx={{
        border: "1px solid rgba(0, 0, 0, 0.2)",
        borderRadius: "30px",
        background: "#FFFFFF",
        boxShadow: "6px 6px 54px 0px #0000000D",
        padding: "20px",
      }}
    >
      <AppBar position="static" color="default" elevation={0} sx={{ mb: 4 }}>
        <Toolbar>
          <PersonSearchIcon sx={{ mr: 2 }} />
          <Typography variant="h6" color="inherit">
            Resume Pool Search
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            gap: 2,
            mb: 3,
            alignItems: isSmallScreen ? "stretch" : "center",
          }}
        >
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Search by skills, requirements, or job description"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{
              height: 40,
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            disabled={loading || !searchKeyword.trim()}
            sx={{
              whiteSpace: "nowrap",
              minWidth: 120,
              fontWeight: 500,
              px: 3,
              boxShadow: "0px 2px 6px rgba(0,0,0,0.15)",
            }}
          >
             {loading ? <CircularProgress size={20} /> : <SearchIcon /> }
          </Button>

          {searchResults.length > 0 && (
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleClear}
              sx={{
                whiteSpace: "nowrap",
                minWidth: 100,
                fontWeight: 500,
                px: 3,
              }}
            >
              Clear
            </Button>
          )}
        </Box>
        {searchResults.length > 0 ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6">
                Total Candidates Fetched: {candidateCount}
              </Typography>
            </Box>
            <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        whiteSpace: "nowrap",
                        maxWidth: "200px",
                      }}
                      align="center"
                    >
                      Candidate Name
                    </TableCell>
                    <TableCell align="center">Reason</TableCell>
                    <TableCell align="center">Phone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((candidate, index) => (
                    <TableRow key={candidate["Resume ID"] || index} hover>
                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        align="center"
                      >
                        {safeRender(candidate["Candidate Name"])}
                      </TableCell>

                      <TableCell align="center">
                        <Tooltip
                          title={candidate["Reason"] || ""}
                          placement="top"
                          arrow
                        >
                          <Box
                            component="span"
                            sx={{
                              display: "inline-block",
                              maxWidth: isSmallScreen ? "150px" : "350px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              textAlign: "center",
                            }}
                          >
                            {safeRender(candidate["Reason"])}
                          </Box>
                        </Tooltip>
                      </TableCell>

                      <TableCell
                        sx={{
                          whiteSpace: "nowrap",
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        align="center"
                      >
                        {safeRender(candidate["Phone"])}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="textSecondary">
              {loading
                ? "Searching..."
                : searchKeyword && !loading
                ? "No matching candidates found"
                : "Enter keywords to search for candidates"}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ResumePoolSearch;
